import React, {FormEvent, useState} from "react"
import {useTranslation} from "react-i18next"
import {alpha, Box, Button as MuiButton, Stack} from "@mui/material"
import {isValidEmail, Participant} from "../../library/Participant"
import Theme from "../../library/Theme"
import {isValidPhoneNumber} from "react-phone-number-input"
import BigButton from "../buttons/BigButton"
import LabeledTextField from "../inputs/LabeledTextField"
import PhoneNumberField from "../inputs/PhoneNumberField"

export function PrivacyPolicyLink({textColor}: { textColor: string }) {
  const {t} = useTranslation()
  const color = alpha(textColor, 0.8)
  return (
    <MuiButton
      variant="text"
      size="small"
      href="https://www.simplebooth.com/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
      sx={{mt: 2, fontSize: 14, color, "&:hover": {color, textDecoration: "underline"}}}
    >
      {t("splash.privacyPolicy")}
    </MuiButton>
  )
}

interface SplashFormProps {
  theme: Theme
  currentParticipant: Participant
  emailOn?: boolean
  firstNameOn?: boolean
  lastNameOn?: boolean
  phoneNumberOn?: boolean
  handleSuccess: (participant: Participant) => void
}

export default function SplashForm({
                                     theme,
                                     currentParticipant,
                                     emailOn,
                                     firstNameOn,
                                     lastNameOn,
                                     phoneNumberOn,
                                     handleSuccess,
                                   }: SplashFormProps) {
  const {t} = useTranslation()
  const textColor = theme.textColor

  const [email, setEmail] = useState<string | null>(currentParticipant.email)
  const [firstName, setFirstName] = useState<string | null>(currentParticipant.firstName)
  const [lastName, setLastName] = useState<string | null>(currentParticipant.lastName)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(currentParticipant.phoneNumber)

  const emailValid = email ? isValidEmail(email) : false
  const firstNameValid = firstName ? firstName.length > 0 : false
  const lastNameValid = lastName ? lastName.length > 0 : false
  const phoneNumberValid = phoneNumber ? isValidPhoneNumber(phoneNumber) : false

  const formValid =
    (emailValid || !emailOn) &&
    (firstNameValid || !firstNameOn) &&
    (lastNameValid || !lastNameOn) &&
    (phoneNumberValid || !phoneNumberOn)

  const handleEmailChange = (value: string) => {
    setEmail(String(value).toLowerCase())
  }

  const handlePhoneChange = (value?: string) => {
    if (!value) return
    setPhoneNumber(value)
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    if (!formValid) return

    handleSuccess({
      ...currentParticipant,
      email: email ?? currentParticipant.email,
      firstName: firstName ?? currentParticipant.firstName,
      lastName: lastName ?? currentParticipant.lastName,
      phoneNumber: phoneNumber ?? currentParticipant.phoneNumber,
      acceptedTerms: new Date(),
    })
  }

  return (
    <Stack direction="column" spacing={2.5} alignItems="center" component="form" onSubmit={submit}>
      {(firstNameOn || lastNameOn) && (
        <Stack direction={{xs: "column", sm: "row"}} spacing={2} sx={{width: "100%"}}>
          {firstNameOn && (
            <LabeledTextField
              label={t("splash.firstName")}
              textColor={textColor}
              type="text"
              name="firstName"
              value={firstName ?? ""}
              placeholder={t("splash.firstNamePlaceholder")}
              aria-label="First name"
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
              containerSx={{flex: 1}}
            />
          )}
          {lastNameOn && (
            <LabeledTextField
              label={t("splash.lastName")}
              textColor={textColor}
              type="text"
              name="lastName"
              value={lastName ?? ""}
              placeholder={t("splash.lastNamePlaceholder")}
              aria-label="Last name"
              onChange={(e) => setLastName(e.target.value)}
              required
              autoComplete="family-name"
              containerSx={{flex: 1}}
            />
          )}
        </Stack>
      )}

      {emailOn && (
        <LabeledTextField
          label={t("splash.emailAddress")}
          textColor={textColor}
          type="email"
          name="email"
          value={email ?? ""}
          placeholder={t("splash.emailPlaceholderAlternate")}
          aria-label="Email"
          onChange={(e) => handleEmailChange(e.target.value)}
          required
          autoComplete="email"
        />
      )}

      {phoneNumberOn && (
        <PhoneNumberField
          label={t("splash.phoneNumber")}
          textColor={textColor}
          value={phoneNumber}
          onChange={handlePhoneChange}
        />
      )}

      <Box sx={{width: "100%", pt: 1.5}}>
        <BigButton
          type="submit"
          disabled={!formValid}
          fullWidth
        >
          {t("splash.continue")}
        </BigButton>
      </Box>
      <PrivacyPolicyLink textColor={textColor}/>
    </Stack>
  )
}
